import * as React from 'react'

import ImageWithStatus from './ImageWithStatus'

import useI18n from '@store/i18n/useI18n'

import styled from '@theme/styled-components'

import apis from '@app/api/api'
import Viewer from 'react-viewer'
import { ImageDecorator } from 'react-viewer/lib/ViewerProps'

interface Props {
  imageId: string
  index: number
  images: DeclarationPicture[] | BusinessDetailsPicture[]
  size?: number
  specialhover?: boolean
  fromdetailbusiness?: boolean
  removingPictures?: boolean
  setSelectedPicture?: () => void
  selected?: boolean
  status?: BusinessDeclarationPicture['status']
  deletePicture?: (imageId: string) => void
}
type Image = { id: string; picture: string }

const Images = ({
  imageId,
  index,
  images,
  size,
  specialhover = true,
  fromdetailbusiness,
  removingPictures,
  setSelectedPicture,
  selected,
  status,
  deletePicture,
}: Props) => {
  const [visible, setVisible] = React.useState(false)
  const [pictures, setPictures] = React.useState<{ [pictureId: string]: string }>({ [imageId]: '' })

  const closeModal = () => setVisible(false)
  const openModal = () => setVisible(true)
  const i18n = useI18n()

  React.useEffect(() => {
    loadPictures(imageId)
  }, [imageId])

  React.useEffect(() => {
    if (visible) {
      loadAllPictures()
    }
  }, [visible])

  const loadPictures = async (pictureId: string) => {
    if (!pictures[pictureId]) {
      apis.declaration.pictures.getPicture(pictureId).then((response) => {
        setPictures((prev) => ({ ...prev, [pictureId]: response }))
      })
    }
  }

  const loadAllPictures = async () => {
    images.map(async (image) => {
      await loadPictures(image.id)
    })
  }

  return (
    <ImageContainer>
      {selected && (
        <IconContainer size={size}>
          <svg width="23" height="23" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="11.5" cy="11.5" r="11.5" fill="#FAB200" />
            <path
              d="M15.2121 8.05584C15.5782 7.68972 16.1718 7.68972 16.5379 8.05584C16.904 8.42195 16.904 9.01555 16.5379 9.38166L10.9129 15.0067C10.5468 15.3728 9.9532 15.3728 9.58709 15.0067L6.46209 11.8817C6.09597 11.5155 6.09597 10.922 6.46209 10.5558C6.8282 10.1897 7.4218 10.1897 7.78791 10.5558L10.25 13.0179L15.2121 8.05584Z"
              fill="#0053A1"
            />
            <mask id="mask0" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="6" y="7" width="11" height="9">
              <path
                d="M15.2121 8.05584C15.5782 7.68972 16.1718 7.68972 16.5379 8.05584C16.904 8.42195 16.904 9.01555 16.5379 9.38166L10.9129 15.0067C10.5468 15.3728 9.9532 15.3728 9.58709 15.0067L6.46209 11.8817C6.09597 11.5155 6.09597 10.922 6.46209 10.5558C6.8282 10.1897 7.4218 10.1897 7.78791 10.5558L10.25 13.0179L15.2121 8.05584Z"
                fill="white"
              />
            </mask>
            <g mask="url(#mask0)"></g>
          </svg>
        </IconContainer>
      )}
      <Image
        key={pictures[imageId]}
        src={pictures[imageId]}
        onClick={!!deletePicture || !removingPictures ? openModal : setSelectedPicture || undefined}
        size={size}
        specialhover={specialhover}
        fromdetailbusiness={fromdetailbusiness}
        selected={selected}
        status={status}
      />
      <Viewer
        visible={visible}
        onClose={() => setVisible(false)}
        images={images.map((i) => ({
          src:
            i.status === 'READY'
              ? pictures[i.id]
              : i.status === 'PENDING'
              ? require('./imgs/encours.png').default
              : require('./imgs/rejected.png').default,
        }))}
        activeIndex={index}
        drag={false}
        scalable={false}
        noImgDetails
        onMaskClick={closeModal}
        changeable
        rotatable={false}
        noToolbar={false}
        customToolbar={() =>
          deletePicture
            ? [
                {
                  key: 'delete',
                  render: (
                    <DeleteContainer>
                      <DeleteButton>{i18n.t('components.detailsChantier.buttons.delete')}</DeleteButton>
                    </DeleteContainer>
                  ),
                  onClick: (imageDecorator: ImageDecorator) => {
                    const image = Object.entries(pictures).find(([_, image]) => imageDecorator.src === image)
                    if (image) {
                      deletePicture(image[0])
                    }
                  },
                },
              ]
            : []
        }
      />
    </ImageContainer>
  )
}

export default Images

const Image = styled(ImageWithStatus)<{
  size?: number
  specialhover?: boolean
  fromdetailbusiness?: boolean
  selected?: boolean
}>`
  width: ${(props) => props.size || (430 - 60) / 3}px;
  height: ${(props) => props.size || 160}px;
  margin: 5px 10px;
  object-fit: cover;
  ${(props) => !!props.fromdetailbusiness && `background-color: ${props.theme.colors.secondary_text};`}
  ${(props) => !!props.fromdetailbusiness && 'padding: 4px;'};
  border-radius: ${(props) => (props.fromdetailbusiness ? '5' : '15')}px;
  ${(props) => !props.fromdetailbusiness && 'filter: drop-shadow(0px 10px 30px rgba(0, 83, 161, 0.15));'}
  cursor: pointer;
  ${(props) => !!props.selected && `background-color: ${props.theme.colors.markerStopped};`}

  &:hover {
    ${(props) =>
      props.specialhover
        ? `
        filter: drop-shadow(0px 10px 10px rgba(0, 83, 161, 0.3));
        transform: scale(1.1, 1.1);
        z-index: 9;
      `
        : null}
  }
`

const IconContainer = styled('div')<{ size?: number }>`
  position: relative;
  z-index: 1;
  left: 25px;
  top: 20px;
  width: 0px;
  background-color: blue;
  z-index: 10;
`
const ImageContainer = styled('div')<{ size?: number }>`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
`
const DeleteButton = styled('div')`
  ${(props) => `background-color: ${props.theme.colors.tabError};`}
  padding: 0px 16px;
  border-radius: 30px;
  box-shadow: 0 12px 35px 0 rgba(33, 75, 115, 0.2);
  bottom: 55px;
`
const DeleteContainer = styled('div')`
  display: flex;
  flex: 1;
  justify-content: center;
`
